import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  LoginServicePayload,
  LoginServiceResponse,
  LoginWithOTPServicePayload,
  LoginWithOTPServiceResponse,
  ValidateOTPServicePayload,
  ValidateOTPServiceResponse,
  EmailVerificaitonServicePayload,
  EmailVerifcationServiceResponse,
  GetMyTeamResponse,
  UpdateAccountServicePayload,
  UserImageServicePayload,
  MyProfileResponse,
  ProfessionalDescriptionItem,
} from "interfaces";
import {
  loginService,
  loginWithOTPService,
  validateOTPService,
  emailVerificationService,
  getProfilePictureService,
  getMyTeamService,
  getUserImageService,
  updateProfilePictureService,
  updateAccountService,
  updateUsernameService,
  getMyProfileService,
  getProfessionalDescriptionService,
} from "services";

export const useLogin = (callBack: (data: LoginServiceResponse) => void) => {
  return useMutation({
    mutationFn: (payload: LoginServicePayload) => loginService(payload),
    onSuccess: (data) => {
      callBack(data);
      // console.log("Login successful", data);
    },
  });
};

export const useValidateOTP = (
  callBack: (data: ValidateOTPServiceResponse) => void,
) => {
  return useMutation({
    mutationFn: (payload: ValidateOTPServicePayload) =>
      validateOTPService(payload),
    onSuccess: (data) => {
      callBack(data);
      // console.log("OTP validation successful", data);
    },
  });
};

export const useLoginWithOTP = (
  callBack: (data: LoginWithOTPServiceResponse) => void,
) => {
  return useMutation({
    mutationFn: (payload: LoginWithOTPServicePayload) =>
      loginWithOTPService(payload),
    onSuccess: (data) => {
      callBack(data);
      // console.log("OTP sent successfully", data);
    },
  });
};

export const useEmailVerification = (
  callBack: (data: EmailVerifcationServiceResponse) => void,
) => {
  return useMutation({
    mutationFn: (payload: EmailVerificaitonServicePayload) =>
      emailVerificationService(payload),
    onSuccess: (data) => {
      callBack(data);
      // console.log("Email verification successful", data);
    },
  });
};

export const useGetProfilePicture = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ["getProfilePicture"],
    queryFn: () => getProfilePictureService(),
    enabled,
  });
};

export const useGetUserImage = (payLoad: UserImageServicePayload) => {
  return useQuery({
    queryKey: ["getUserImage"],
    queryFn: () => getUserImageService(payLoad),
    enabled: true,
  });
};

export const useUpdateProfilePicture = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => updateProfilePictureService(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["getProfilePicture"] });
    },
  });
};

export const useUpdateAccount = (callBack?: () => void) => {
  return useMutation({
    mutationFn: (payload: UpdateAccountServicePayload) =>
      updateAccountService(payload),
    onSuccess: () => {
      callBack?.();
    },
  });
};

export const useGetMyProfile = (enabled: boolean = true) => {
  return useQuery<MyProfileResponse>({
    queryKey: ["myProfile"],
    queryFn: () => getMyProfileService(),
    enabled,
  });
};

export const useGetProfessionalDescription = (enabled: boolean = true) => {
  return useQuery<ProfessionalDescriptionItem[]>({
    queryKey: ["professionalDescription"],
    queryFn: () => getProfessionalDescriptionService(),
    enabled,
  });
};

export const useUpdateUsername = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (username: string) => updateUsernameService(username),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["userSettings"] });
    },
  });
};

export const useGetMyTeam = (enabled: boolean = true) => {
  return useQuery<GetMyTeamResponse>({
    queryKey: ["my-team"],
    queryFn: () => getMyTeamService(),
    enabled,
  });
};
